/**
 * This Library is used to define TalkingParrot related functions to be shared amongst components/libraries
 */
import { Base64 } from 'js-base64';
import { DateTime } from 'luxon';

export const ROOM_STATUS_LOADING = 'ROOM_LOADING';
export const ROOM_STATUS_READY = 'ROOM_STATUS_READY';
export const ROOM_STATUS_END = 'ROOM_STATUS_END';

const ROOM_TYPES = {
  SMALL: 'SMALL', // 4 participants p2p
  LARGE: 'LARGE', // 50 participants not p2p
};

/**
 * @param {object} appointment
 * @param {number} appointment.uniqueIdForUrl
 * @returns {string} unique hashed Id for a given appointments occurrence
 */
const getVideoServiceId = ({ uniqueIdForUrl }) => {
  if (!uniqueIdForUrl) {
    throw new Error('getVideoServiceId parameter uniqueIdForUrl is undefined');
  }

  return Base64.encode(uniqueIdForUrl);
};

/**
 * @param {object} appointment
 * @param {number} appointment.uniqueIdForUrl
 * @returns {URL}
 */
const authVideoSessionUrl = ({ uniqueIdForUrl }) => {
  let videoServiceId = getVideoServiceId({ uniqueIdForUrl });
  return new URL(
    `/telehealth-ui/#/session/${videoServiceId}`,
    window.location.origin,
  );
};

/**
 * @param {object} appointment
 * @param {number} appointment.uniqueIdForUrl
 * @returns {URL}
 */
const publicVideoSessionUrl = ({ uniqueIdForUrl }) => {
  let videoServiceId = getVideoServiceId({ uniqueIdForUrl });
  return new URL(`/telehealth-ui/#/${videoServiceId}`, window.location.origin);
};

/**
 *
 * @param {object} appointment
 * @param {number} appointment.uniqueIdForUrl
 * @param {boolean} roomType - whether the room needs to be larger than 4 particiants
 * @returns {URL}
 */
const betaAppointmentUrl = ({ uniqueIdForUrl }, largeRoom) => {
  let videoServiceId = getVideoServiceId({ uniqueIdForUrl });
  let url = new URL(
    `/talking-parrot-ui/api/appointment/${videoServiceId}`,
    window.location.origin,
  );

  // use /telehealth-ui/{appointmentUUID}/1 to get a large room
  let roomType = !!largeRoom ? ROOM_TYPES.LARGE : null;
  url.search = new URLSearchParams({
    ...(!!roomType ? { type: roomType } : {}),
  });

  return url;
};

/**
 *
 * @param {object} appointment
 * @param {number} appointment.uniqueIdForUrl
 * @param {boolean} roomType - whether the room needs to be larger than 4 particiants
 * @returns {URL}
 */
const authAppointmentUrl = ({ uniqueIdForUrl }, largeRoom) => {
  let videoServiceId = getVideoServiceId({ uniqueIdForUrl });
  let url = new URL(
    `/telehealth-ui/api/appointmentUrl/user/${videoServiceId}`,
    window.location.origin,
  );

  // use /telehealth-ui/{appointmentUUID}/1 to get a large room
  let roomType = !!largeRoom ? ROOM_TYPES.LARGE : null;
  url.search = new URLSearchParams({
    ...(!!roomType ? { type: roomType } : {}),
  });

  return url;
};

export const DOB_INPUT_FORMAT = 'MM/dd/yyyy';
export const DOB_API_FORMAT = 'yyyy-MM-dd';
/**
 * converts ui string to string for API
 * @param {string} dob in format MM/dd/yyyy
 * @returns {string} in format yyyy-MM-dd
 */
export function dobStringConversion(dob) {
  return DateTime.fromFormat(dob, DOB_INPUT_FORMAT).toFormat(DOB_API_FORMAT);
}
/**
 *
 * @param {object} appointment
 * @param {number} appointment.uniqueIdForUrl
 * @param {string} dob
 * @param {boolean} largeRoom
 */
const publicAppointmentUrl = ({ uniqueIdForUrl }, dob, largeRoom) => {
  if (!uniqueIdForUrl || !dob) {
    throw new TypeError(
      'publicAppointmentUrl requires an appointment with uniqueIdForUrl as a property and a dob of birth as a string',
    );
  }
  let videoServiceId = getVideoServiceId({ uniqueIdForUrl });
  let url = new URL(
    `/telehealth-ui/api/appointmentUrl/${videoServiceId}`,
    window.location.origin,
  );

  let roomType = !!largeRoom ? ROOM_TYPES.LARGE : null;
  let dobParam = !!dob ? dobStringConversion(dob) : null;

  url.search = new URLSearchParams({
    ...(!!roomType ? { type: roomType } : {}),
    ...(!!dobParam ? { dob: dobParam } : {}),
  });

  return url;
};

export default {
  authAppointmentUrl,
  betaAppointmentUrl,
  publicAppointmentUrl,
  authVideoSessionUrl,
  publicVideoSessionUrl,
};
